import React, { useState } from 'react';
import { Plus, Trash2, RotateCcw } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { formatNumber } from '../utils';

export function ResetConfirmModal({ onCancel, onConfirm }) {
  const { language } = useLanguage();
  return (
    <div className="absolute inset-0 z-50 bg-white/90 backdrop-blur-sm flex items-center justify-center p-4 rounded-xl">
      <div className="bg-white p-6 rounded-xl shadow-2xl border-2 border-gray-200 max-w-sm w-full text-center space-y-4">
        <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto">
          <RotateCcw size={24} />
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-bold text-gray-900">
            {language === 'es' ? '¿Reiniciar Plan?' : 'Reset Plan?'}
          </h3>
          <p className="text-sm text-gray-500">
            {language === 'es' 
              ? 'Esto restablecerá la inversión inicial. Esta acción se puede deshacer.' 
              : 'This will reset the initial investment. This action can be undone.'}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-lg transition-colors text-sm"
          >
            {language === 'es' ? 'Cancelar' : 'Cancel'}
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg transition-colors text-sm"
          >
            {language === 'es' ? 'Confirmar' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ExtraCapitalModal({ extraDeposits, onAdd, onDeleteDeposit, onClose }) {
  const { language } = useLanguage();
  const [depositInput, setDepositInput] = useState('');
  const [depositDate, setDepositDate] = useState(() => new Date().toISOString().slice(0, 10));

  const handleAdd = () => {
    const amount = parseFloat(depositInput);
    if (amount > 0) {
      onAdd(amount, depositDate);
      setDepositInput('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-5 sm:p-6" onClick={(e) => e.stopPropagation()} data-testid="extra-balance-modal">
        <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-1">
          <Plus size={18} className="text-purple-600" />
          {language === 'es' ? 'Agregar Capital Extra' : 'Add Extra Capital'}
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          {language === 'es' 
            ? 'El capital se suma a tu cuenta a partir de la fecha que coloques: no afecta los trades anteriores, solo los cálculos desde esa fecha.'
            : 'The capital is added to your account from the date you set: it does not affect previous trades, only calculations from that date on.'}
        </p>
        <div className="mb-3">
          <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">
            {language === 'es' ? 'Fecha del depósito' : 'Deposit date'}
          </label>
          <input
            type="date"
            value={depositDate}
            onChange={(e) => setDepositDate(e.target.value)}
            className="w-full px-3 py-2 border border-purple-200 rounded-lg text-sm font-semibold text-gray-700 focus:ring-2 focus:ring-purple-500 outline-none"
            data-testid="extra-balance-date-input"
          />
        </div>
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-400 font-bold">$</span>
            <input
              type="text"
              inputMode="decimal"
              value={depositInput}
              onChange={(e) => setDepositInput(e.target.value.replace(/[^0-9.]/g, ''))}
              className="w-full pl-7 pr-3 py-2.5 border border-purple-200 rounded-lg font-bold text-purple-700 focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder="0.00"
              data-testid="extra-balance-input"
            />
          </div>
          <button
            onClick={handleAdd}
            disabled={!parseFloat(depositInput)}
            className="px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg transition-colors disabled:opacity-40 text-sm"
            data-testid="extra-balance-add-btn"
          >
            {language === 'es' ? 'Agregar' : 'Add'}
          </button>
        </div>
        
        {extraDeposits.length > 0 && (
          <div className="border-t border-gray-100 pt-3 space-y-2 max-h-44 overflow-y-auto">
            <p className="text-xs font-bold text-gray-500 uppercase">{language === 'es' ? 'Depósitos agregados' : 'Added deposits'}</p>
            {extraDeposits.map((d) => (
              <div key={d.id} className="flex items-center justify-between bg-purple-50 rounded-lg px-3 py-2" data-testid="deposit-item">
                <div>
                  <p className="text-sm font-bold text-purple-700">${formatNumber(d.amount)}</p>
                  <p className="text-[10px] text-gray-400">{d.date} · {language === 'es' ? 'desde el trade' : 'from trade'} {(d.afterTrade || 0) + 1}</p>
                </div>
                <button
                  onClick={() => onDeleteDeposit && onDeleteDeposit(d.id)}
                  className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                  data-testid="deposit-delete-btn"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
        
        <button
          onClick={onClose}
          className="w-full mt-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold rounded-lg text-sm transition-colors"
          data-testid="extra-balance-close-btn"
        >
          {language === 'es' ? 'Cerrar' : 'Close'}
        </button>
      </div>
    </div>
  );
}
